import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import { CustomTooltip } from '@components/CustomTooltip'
import alertsApi from '@modules/alerts/api'
import { ALERT_FREQUENCY_TO_LABEL_MAP } from '@modules/alerts/constants'
import { ProfileType } from '@modules/search/types'
import shortlistApi from '@modules/shortlist/api'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import { AlertFrequencyType, ProfileAlert } from '../../types'
import ProfileAlertPopover from '../ProfileAlertPopover'
import { IconWrapper, ProfileAlertButtonsWrapper } from '../index.styles'

interface ProfileAlertButtonButtonProps {
  profile: { id: number; '@type': ProfileType }
  stretch?: boolean
  alertsShortlistId?: number
  withLastViewedUpdate?: boolean
  savedProfileAlert?: ProfileAlert
  isPrivate?: boolean
}

const ProfileAlertButton = ({
  profile,
  isPrivate,
  stretch = false,
  alertsShortlistId,
  withLastViewedUpdate,
  savedProfileAlert,
}: ProfileAlertButtonButtonProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [profileAlert, setProfileAlert] = useState<ProfileAlert>()
  const [addAlertPopoverAnchor, setAddAlertPopoverAnchor] = useState<Nullable<HTMLElement>>(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const [emailTooltipOpen, setEmailTooltipOpen] = useState(false)
  const [systemAlertTooltipOpen, setSystemTooltipOpen] = useState(false)

  const hasAlert = useMemo(() => Boolean(profileAlert), [profileAlert])
  const hasScheduledAlert = useMemo(() => Boolean(profileAlert?.alertFrequency), [profileAlert])

  const alertAddedTooltipTitle = useMemo(() => {
    if (hasAlert && !hasScheduledAlert) {
      return t(translationKeys.ALERT_ADDED)
    }
    if (hasAlert && hasScheduledAlert) {
      return `${t(ALERT_FREQUENCY_TO_LABEL_MAP[profileAlert?.alertFrequency as AlertFrequencyType])} ${t(
        translationKeys.ALERT_WITH_NOTIFICATION_ADDED,
      )}`
    }

    return ''
  }, [t, hasAlert, hasScheduledAlert, profileAlert?.alertFrequency])

  const { mutate: updateProfileAlertViewed } = useMutation({
    mutationFn: alertsApi.updateProfileAlertViewed.fn,
    onSuccess: () => {
      invalidateShortlistData()
    },
  })

  useEffect(() => {
    setProfileAlert(savedProfileAlert)
  }, [savedProfileAlert])

  useEffect(() => {
    if (withLastViewedUpdate && profileAlert) {
      updateProfileAlertViewed([profile])
    }
  }, [withLastViewedUpdate, profileAlert?.searchDocId])

  const invalidateShortlistData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })

    if (alertsShortlistId) {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, alertsShortlistId] })
    } else {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key] })
    }
  }, [queryClient, alertsShortlistId])

  const { mutate: createProfilesAlert } = useMutation({
    mutationFn: alertsApi.createProfileAlert.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === alertsApi.fetchProfilesAlerts.key && Array.isArray(query.queryKey[1])
            ? query.queryKey[1].includes(profile.id)
            : query.queryKey[1] === profile.id,
      })
      invalidateShortlistData()
      queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key] })
    },
  })

  const { mutate: deleteProfilesAlerts } = useMutation({
    mutationFn: alertsApi.deleteProfilesAlerts.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === alertsApi.fetchProfilesAlerts.key && Array.isArray(query.queryKey[1])
            ? query.queryKey[1].includes(profile.id)
            : query.queryKey[1] === profile.id,
      })
      invalidateShortlistData()
      queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key] })
    },
  })

  const handleOpenAddAlertPopover = (event: React.MouseEvent<HTMLElement>) => {
    if (event.currentTarget) {
      setAddAlertPopoverAnchor(event.currentTarget)
      setEmailTooltipOpen(true)
    }
  }

  const handleSaveAlertFrequency = useCallback(
    (frequency?: AlertFrequencyType) => {
      createProfilesAlert({
        profileAlerts: [
          {
            id: profile.id,
            type: profile['@type'],
          },
        ],
        alertFrequency: frequency === AlertFrequencyType.NO_NOTIFICATION ? undefined : frequency,
      })

      setAddAlertPopoverAnchor(null)
    },
    [createProfilesAlert],
  )

  const handleEdit = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAddAlertPopoverAnchor(event?.currentTarget)
  }, [])

  const handleSystemAlertClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasScheduledAlert) {
      handleEdit(event)
      return
    }

    if (hasAlert) {
      deleteProfilesAlerts([profile])
      return
    }

    handleSaveAlertFrequency()
  }

  const handleDelete = useCallback(() => {
    setShowDeleteConfirmation(false)
    setAddAlertPopoverAnchor(null)
    deleteProfilesAlerts([profile])
    setEmailTooltipOpen(false)
  }, [])

  const handleSaveCancel = useCallback(() => {
    setAddAlertPopoverAnchor(null)
    setEmailTooltipOpen(false)
    setSystemTooltipOpen(false)
  }, [])

  return (
    <>
      {!isPrivate ? (
        <ProfileAlertButtonsWrapper stretch={stretch}>
          <>
            <CustomTooltip
              title={
                hasAlert
                  ? t(translationKeys.ADD_ALERTS_IN_APP_ALERT_ADDED_TOOLTIP)
                  : t(translationKeys.ADD_ALERTS_IN_APP_ALERT_TOOLTIP)
              }
              open={systemAlertTooltipOpen}
              onClose={() => setSystemTooltipOpen(false)}
              placement="top-end"
            >
              <IconWrapper
                onClick={handleSystemAlertClick}
                onMouseEnter={() => setSystemTooltipOpen(true)}
                onMouseLeave={() => setSystemTooltipOpen(false)}
                $hasBackground={hasAlert || hasScheduledAlert}
              >
                <NotificationsNoneOutlinedIcon
                  fontSize="small"
                  htmlColor={hasAlert || hasScheduledAlert ? colors.white : colors.primary}
                />
              </IconWrapper>
            </CustomTooltip>
            <CustomTooltip
              title={hasScheduledAlert ? alertAddedTooltipTitle : t(translationKeys.ADD_ALERTS_EMAIL_ALERT_TOOLTIP)}
              open={emailTooltipOpen}
              onClose={() => setEmailTooltipOpen(false)}
              placement="top-start"
            >
              <IconWrapper
                onMouseEnter={() => setEmailTooltipOpen(true)}
                onMouseLeave={() => setEmailTooltipOpen(false)}
                onClick={hasScheduledAlert ? handleEdit : handleOpenAddAlertPopover}
                $hasBackground={hasScheduledAlert}
                $hovered={!!addAlertPopoverAnchor}
              >
                <EmailOutlinedIcon fontSize="small" htmlColor={hasScheduledAlert ? colors.white : colors.primary} />
              </IconWrapper>
            </CustomTooltip>
          </>
        </ProfileAlertButtonsWrapper>
      ) : null}
      {addAlertPopoverAnchor && (
        <ProfileAlertPopover
          anchor={addAlertPopoverAnchor}
          isEdit={hasScheduledAlert}
          preselectedAlertFrequency={profileAlert?.alertFrequency}
          onDelete={() => setShowDeleteConfirmation(true)}
          onSave={handleSaveAlertFrequency}
          onCancel={handleSaveCancel}
        />
      )}
      {showDeleteConfirmation && (
        <ConfirmPopup
          id="profile-alert-confirm-delete-popup"
          width={294}
          contentWidth={180}
          isOpen
          title={t(translationKeys.DELETE_ALERTS_TITLE)}
          text={t(translationKeys.DELETE_ALERTS_SUBTITLE)}
          cancelBtnText={t(translationKeys.CANCEL_BUTTON)}
          confirmBtnText={t(translationKeys.DELETE_BUTTON)}
          onCancel={() => {
            setShowDeleteConfirmation(false)
            setEmailTooltipOpen(false)
          }}
          onConfirm={handleDelete}
        />
      )}
    </>
  )
}

export default memo(ProfileAlertButton)
